import { useCallback } from "react";
import { flushSync } from "react-dom";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { Invoice } from "~/shared/schemas/invoice-schema.ts";
import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";

import type { ConfirmationData } from "../confirmation-modal/index.tsx";
import {
	formatInvalidInvoiceAmountMessage,
	getInvalidAmountInvoices,
} from "../modal/utils/has-invalid-invoice-amount.ts";
import { usePaymentExecution } from "./use-payment-execution.ts";

type UseHandlePaymentProps = {
	selectedInvoices: Invoice[];
	selectedPaymentValue: SavedCard | SavedAch | PaymentNonce | null;
	clearSelection: () => void;
	closeModal: () => void;
	setConfirmationData: (data: ConfirmationData) => void;
};

export function useHandlePayment({
	selectedInvoices,
	selectedPaymentValue,
	clearSelection,
	closeModal,
	setConfirmationData,
}: UseHandlePaymentProps) {
	const { executePayment, isSubmitting } = usePaymentExecution();
	const { showWarning } = useNotificationActions();

	const handleExecutePayment = useCallback(
		async (paymentData: {
			amounts: number;
			invoiceAmounts: Record<string, number>;
			description: string;
			isLumpSum: boolean;
		}) => {
			// Guard against submitting an empty non-lump-sum payment. This can
			// happen if every selected invoice was filtered out as ineligible
			// before the modal submitted.
			if (!paymentData.isLumpSum && selectedInvoices.length === 0) {
				showWarning(
					"No eligible invoices are selected. Please select at least one invoice to pay.",
				);
				return;
			}

			const invalidInvoices = paymentData.isLumpSum
				? []
				: getInvalidAmountInvoices(
						selectedInvoices,
						paymentData.invoiceAmounts,
						paymentData.amounts,
					);
			if (invalidInvoices.length > 0) {
				showWarning(formatInvalidInvoiceAmountMessage(invalidInvoices));
				return;
			}

			await executePayment(
				selectedInvoices,
				selectedPaymentValue,
				paymentData,
				(result) => {
					const apiResult = result as
						| Record<string, unknown>
						| null
						| undefined;
					const pv = selectedPaymentValue;
					let paymentAccount: string | undefined;
					if (pv) {
						const last4Suffix = pv.Last4 ? ` ending in ${pv.Last4}` : "";
						if (pv.paymentMethod === "ACH") {
							paymentAccount = `ACH${last4Suffix}`;
						} else {
							const cardLabel = "cardType" in pv ? String(pv.cardType) : "Card";
							paymentAccount = `${cardLabel}${last4Suffix}`;
						}
					}
					const firstInvoice = selectedInvoices[0];
					const confirmationDataValue: ConfirmationData = {
						confirmationNumber:
							typeof apiResult?.["confirmationNumber"] === "string"
								? apiResult["confirmationNumber"]
								: undefined,
						paymentAmount: paymentData.amounts,
						paymentAccount,
						dueDate: firstInvoice?.dueDate ?? undefined,
						notes: paymentData.description || undefined,
						paidOnInvoices: paymentData.isLumpSum
							? []
							: selectedInvoices
									.map((inv) => String(inv.poNumber ?? ""))
									.filter(Boolean),
						// Derived from existing selectedInvoices/paymentData, not a new data source.
						paidInvoiceCount: paymentData.isLumpSum
							? undefined
							: selectedInvoices.length,
						isLumpSum: paymentData.isLumpSum,
					};
					// Force the payment modal's Kendo Dialog to fully unmount and
					// clean up its overlay before we mount the confirmation
					// modal's Dialog. Without this, both Dialogs are swapped in a
					// single React commit and the unmounting Dialog's `.k-overlay`
					// can be left behind, blocking pointer events on the page.
					flushSync(() => {
						clearSelection();
						closeModal();
					});
					setConfirmationData(confirmationDataValue);
				},
			);
		},
		[
			executePayment,
			selectedInvoices,
			selectedPaymentValue,
			clearSelection,
			closeModal,
			setConfirmationData,
			showWarning,
		],
	);

	return { handleExecutePayment, isSubmitting };
}
